import {
  Button,
  Form,
  FormGroup,
  Modal,
  ModalVariant,
  TextInput,
} from '@patternfly/react-core'
import { ExclamationCircleIcon } from '@patternfly/react-icons'
import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import CanvasDraw from 'react-canvas-draw'
import styled from 'styled-components'

import { theme } from '@app/utils/theme'

import { ScreenContent } from './content-layout'
import { ClearButton, ContinueButton } from './controls'

const MessageContainer = styled.div`
  flex: 4;
  display: flex;
  flex-direction: column;
  align-content: center;
  justify-content: start;
`

const ScreenHeaderRow = styled.div`
  width: 100%;
  display: flex;
  margin-top: 1rem;
  margin-bottom: 1rem;
  padding: 1rem;
  justify-content: center;
`
const ScreenHeader = styled.div`
  text-align: center;
  color: ${(props) => props.theme.colors.primary};
  font-size: 16pt;
  font-weight: 500;
`

const SignatureContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  max-width: 500px;
`

const Signature = styled.div`
  border: 1px solid ${(props) => props.theme.colors.primary};
  border-radius: 10px;
  margin-bottom: 1rem;
  padding-left: 5px;
`

const StaffControlRow = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  padding: 1rem 0;

  h2 {
    margin-bottom: 1rem;
    font-weight: 500;
    font-size: 16pt;
  }
`

const Control = styled.div`
  justify-content: center;
  align-items: center;
`

interface Props {
  onComplete?: (pin: string, signature: string) => void
  message: string
  hasValidWitness?: boolean
}

export const IntakeCompleteModal = function ({
  onComplete,
  message,
  hasValidWitness,
}: Props) {
  const canvasRef = React.createRef<CanvasDraw>()
  const [signature, setSignature] = useState<string>(null)
  const [isWitnessModalOpen, setIsWitnessModalOpen] = useState<boolean>(false)
  const [userPin, setUserPin] = useState<string>('')
  const [isFormValid, setIsFormValid] = useState<boolean>(false)

  useEffect(() => {
    setIsFormValid(signature !== null && userPin.length > 0)
  }, [signature, userPin])

  const onWitnessModalToggle = useCallback(() => {
    setIsWitnessModalOpen(!isWitnessModalOpen)
  }, [isWitnessModalOpen])

  const isWitnessValid = useCallback((valid?: boolean) => {
    if (valid === undefined) {
      return 'default'
    } else {
      return valid ? 'success' : 'error'
    }
  }, [])

  const saveSignature = useCallback(() => {
    if (canvasRef.current) {
      setSignature(canvasRef.current.canvas.drawing.toDataURL())
    }
  }, [canvasRef])

  const clearSignature = useCallback(() => {
    if (canvasRef.current) {
      canvasRef.current.clear()
      setSignature(null)
    }
  }, [canvasRef])

  const onPinChange = useCallback((pin: string) => {
    setUserPin(pin)
  }, [])

  const renderHeader = useCallback((header?: string) => {
    if (header) {
      return (
        <ScreenHeaderRow>
          <ScreenHeader dangerouslySetInnerHTML={{ __html: header }} />
        </ScreenHeaderRow>
      )
    } else {
      return <ScreenHeaderRow />
    }
  }, [])

  const renderSignatureCanvas = useCallback(() => {
    return (
      <Signature>
        <CanvasDraw
          onChange={() => saveSignature()}
          ref={canvasRef}
          brushRadius={2}
          lazyRadius={4}
          hideGrid={true}
          canvasHeight={200}
          canvasWidth={490}
          caternaryColor={theme.colors.primary}
        />
      </Signature>
    )
  }, [canvasRef, saveSignature])

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      onComplete(userPin, signature)
    },
    [onComplete, userPin, signature],
  )

  const handleWitnessClicked = useCallback(() => {
    onComplete(userPin, signature)
  }, [onComplete, userPin, signature])

  const renderCompleteButton = () => {
    return (
      <ContinueButton onClick={onWitnessModalToggle}>Complete</ContinueButton>
    )
  }

  return (
    <ScreenContent>
      <MessageContainer>{renderHeader(message)}</MessageContainer>
      <StaffControlRow>
        <h2>Staff Only</h2>
        <Control>{renderCompleteButton()}</Control>
      </StaffControlRow>
      <Modal
        variant={ModalVariant.small}
        title="Staff - Witness"
        isOpen={isWitnessModalOpen}
        onClose={onWitnessModalToggle}
        actions={[
          <Button
            key="confirm"
            variant="primary"
            isDisabled={!isFormValid}
            onClick={handleWitnessClicked}
          >
            Witness
          </Button>,
          <Button key="cancel" variant="link" onClick={onWitnessModalToggle}>
            Cancel
          </Button>,
        ]}
      >
        <SignatureContainer>
          {renderSignatureCanvas()}
          <ClearButton onClick={() => clearSignature()}>
            Clear Signature
          </ClearButton>
        </SignatureContainer>
        <Form isHorizontal onSubmit={handleSubmit}>
          <FormGroup
            label="PIN"
            isRequired
            fieldId="security-pin"
            validated={isWitnessValid(hasValidWitness)}
            helperTextInvalid="Incorrect PIN. Please try again."
            helperTextInvalidIcon={<ExclamationCircleIcon />}
            helperText="Please enter your security PIN"
          >
            <TextInput type="password" value={userPin} onChange={onPinChange} />
          </FormGroup>
        </Form>
      </Modal>
    </ScreenContent>
  )
}
